/* eslint-disable @typescript-eslint/no-explicit-any */
import { Actions, Async, F1, FSpread, SMap } from '@client/new/helpers/types';
import {
  EditWorkoutModel,
  FormErrors,
  Hint,
  useEditWorkoutModel,
} from '../models/workoutEdit.model';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useEffect, useState } from 'react';
import ajax from '@client/components/ajax';
import {
  TagOptions,
  WorkoutItemTemplates,
  exercisesToTemplates,
  getCurrentBreadcrumb,
  getTemplateSelection,
  mapTagsToOptions,
} from './mappers';
import { useWorkoutClipboardStore } from '@client/new/stores/workoutClipboard.store';
import {
  BuilderType,
  Media,
  RunningExerciseType,
  Template,
  WorkoutItem,
} from '../models/workout.domain';
import _, { isEmpty } from 'lodash';
import {
  useGetCollectionsQuery,
  useGetLibraryContentQuery,
} from 'generated/graphql';
import { navigate, PATHS } from '@client/routes';
import { trackEvent } from '@services/tracking';
import { exerciseTypeToString, isAutoplayedItem } from '../models/mappers';
import { LibraryVideo } from '../models/useVideos';
import { ContentFilters } from '@client/new/common/library/helpers';
import { ExerciseSettings } from '../../workout-editor/view-model/workouts.domain';
import { WorkoutDispatch } from '../../workout-editor/view-model/workout.viewModel';
import { ExerciseSuggestion } from '@client/new/pages/creator/library/models/exercise-suggestions.model';
import { PayloadMap as ModelPayloadMap } from '../models/workoutEdit.model';
import { useUnsavedChangesGuard } from '@client/new/hooks/useUnsavedChangesGuard';

type PayloadMap = {
  showAutoplayedSettingsModal: { exerciseId?: string };
  hideAutoplayedSettingsModal: null;
  applyAutoplayedSettings: ExerciseSettings;
  createAutoplayed: {
    type: 'Tabata' | 'HIIT';
    sets: number;
    time: number;
    rest: number;
  };
  save: never;
  publish: never;
  unpublish: never;
  archive: never;
  addTemplate: Template;
  uploadMedia: File;
  navigateToWorkouts: never;
  removeIntro: never;
  removeThumbnail: never;
  addToClipboard: WorkoutItem;
  removeWorkoutItem: { index: number; exerciseId: string };
  addFromClipboard: WorkoutItem[];
  changeOrderInWorkout: { index: number; order: number };
  changeOrderInCircuit: { circuitIndex: number; index: number; order: number };
  addCircuitSet: { circuitIndex: number; index: number };
  addSet: number;
  removeSet: { exercise: number; set: number };
  removeCircuitSet: { circuitIndex: number; exercise: number; set: number };
  setSetsUnit: { circuitIndex?: number; exercise: number; unit: string };
  addMedia: { media: object; index: number; circuitIndex?: number };
  removeMedia: { index: number; isYoutube?: boolean; circuitIndex?: number };
  addWorkoutCircuitItem: number;
  removeCircuitItem: { circuitIndex: number; exerciseIndex: number };
  setTimeZone: string;
  setPublishedAt: string;
  clearSchedule: never;
  addRunCompleteAudio: { index: number; uuid: string; file: File };
  addSetAudio: {
    exerciseIndex: number;
    uuid: string;
    setIndex: number;
    file: File;
  };
  addOutdoorSet: number;
  addTreadmillSet: number;
  changeRunningVariant: { index: number; variant: RunningExerciseType };
  removeSetAudio: {
    exerciseIndex: number;
    setIndex: number;
  };
  removeRunCompleteAudio: number;
  exerciseTitleUpdate: {
    circuitIndex?: number;
    index: number;
    description: string;
    uuid: string;
  };
  applyHint: { circuitIndex?: number; index: number; hint: Hint; uuid: string };
  openClipboard: never;
  openLibrary: {
    index?: number;
    circuitIndex?: number;
    type?: 'short' | 'long';
    action: 'update' | 'add';
    parentId?: string;
    exerciseId?: string;
  };
  closeLibraryModal: never;
  addTemplatesFromVideos: string[];
  updateExerciseMediaFromSelection: string[];
  addVideo: { uuid: string };
  openExerciseTypeInfo: never;
  updateAutoplayedSettings: SMap<ExerciseSettings>;
  updateAutoplayedTitlesMap: SMap<string>;
  clearRecommendations: never;
  setExerciseSearch: string;
} & Pick<ModelPayloadMap, 'changeMeasurementMode'>;

type EditWorkoutVMAction = Actions<PayloadMap>;
export type EditWorkoutVMDispatcher = FSpread<PayloadMap>;
export type EditWorkoutVMDispatchCb = (action: WorkoutDispatch) => void;
export type MenuItems = Array<{
  label: string;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  activeInReadonly?: boolean;
}>;

export type EditWorkoutVM = {
  setDispatchCb: (v: EditWorkoutVMDispatchCb) => void;
  model: EditWorkoutModel;
  autoplayedSettingsModal: {
    isOpen: boolean;
    value?: ExerciseSettings;
  };
  dispatch: EditWorkoutVMDispatcher;
  templateSelection: Array<{
    label: string;
    icon: IconProp;
    className: string;
    onClick: () => void;
  }>;
  influencerId: number;
  submitting: boolean;
  isSaving: boolean;
  programs: Array<{ label: string; value: number; disabled?: boolean }>;
  programsLoading: boolean;
  currentBreadcrumb: string;
  intro: {
    src: string | null;
    loading: boolean;
  };
  thumbnail: {
    src: string | null;
    loading: boolean;
  };
  exerciseTemplates: WorkoutItemTemplates;
  addFromClipboard: (values: WorkoutItem[]) => void;
  tagOptions: TagOptions;
  isClass: boolean;
  withSwaps: boolean;
  addTemplateFromLibrary: {
    isOpen: boolean;
    onClose: () => void;
    loading: boolean;
    type: ContentFilters | null;
    action: 'add' | 'update';
    onAddVideo: (value: { uuid: string }) => void;
    onAddSelection: (videos: { uuid: string }[]) => void;
  };
  isNewForm: boolean;
  publishAction: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  readonly: boolean;
  workoutInfoId: string;
  errorMessage?: string;
  newWorkoutErrors: SMap<string>;
  expandExercises: number[];
  singleExercisePublishModal: {
    isOpen: boolean;
    onClose: () => void;
    onContinue: () => void;
  };
  exerciseTypesInfoModal: {
    isOpen: boolean;
    onClose: () => void;
  };
  unsavedChangesModal: {
    isOpen: boolean;
    isSaving: boolean;
    onSave: () => void;
    onDiscard: () => void;
    onCancel: () => void;
  };
  titlesMap: SMap<string>;
  exerciseSuggestions: ExerciseSuggestion[];
} & Pick<EditWorkoutModel, 'watch' | 'control' | 'inputHints'>;

export type UseEditWorkoutVMParams = {
  influencerId: number;
  workoutId?: number;
  builderType: BuilderType;
  locationQuery: Record<string, string>;
};

export const useEditWorkoutVM = ({
  influencerId,
  workoutId,
  builderType,
  locationQuery,
}: UseEditWorkoutVMParams): Async<EditWorkoutVM> => {
  const { copy } = useWorkoutClipboardStore();
  const [programs, setPrograms] = useState<
    Array<{ label: string; value: number; disabled?: boolean }>
  >([]);
  const [autoplayedSettings, setAutoplayedSettings] = useState<
    SMap<ExerciseSettings>
  >({});
  const [dispatchCb, setDispatchCb] = useState<F1<WorkoutDispatch>>();
  const [tagOptions, setTagOptions] = useState<TagOptions>([]);
  const [singlePublishModal, setSinglePublishModal] = useState({
    isOpen: false,
    confirmed: false,
  });
  const [autoplayedSettingsModal, setAutoplayedSettingsModal] = useState<{
    isOpen: boolean;
    value?: ExerciseSettings;
  }>({
    isOpen: false,
  });
  const [exerciseTypeInfo, setExerciseTypeInfo] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [autoplayedTitlesMap, setAutoplayedTitlesMap] = useState<SMap<string>>(
    {},
  );
  const [libraryState, setLibraryState] = useState<{
    isOpen: boolean;
    index?: number;
    circuitIndex: null | number;
    type: ContentFilters | null;
    action: 'add' | 'update';
    parentId?: string;
    exerciseId?: string;
  }>({
    isOpen: false,
    index: 0,
    circuitIndex: null,
    type: null,
    action: 'add',
  });
  const {
    data: libraryData,
    loading: libraryLoading,
    refetch: refetchLibrary,
  } = useGetLibraryContentQuery({
    variables: {
      creatorId: influencerId,
    },
    fetchPolicy: 'network-only',
  });

  const asyncModel = useEditWorkoutModel({
    influencerId: influencerId,
    workoutId,
    builderType,
    locationQuery,
  });

  const { data: collectionsData, loading: programsLoading } =
    useGetCollectionsQuery({
      variables: {
        influencerId,
        withDeleted: false,
      },
      fetchPolicy: 'no-cache',
    });

  useEffect(() => {
    if (!collectionsData?.collections) return;

    setPrograms(
      collectionsData?.collections.map((collection) => {
        const isArchived = !!collection.archivedAt;
        const isDraft = !collection.isVisible && !isArchived;
        const suffix = isArchived ? ' (archived)' : isDraft ? ' (draft)' : '';
        const isDisabled = isArchived;

        return {
          label: (collection.name || '') + suffix,
          value: collection.id,
          disabled: isDisabled,
        };
      }),
    );
    ajax.fetchAvailableWorkoutTags().then((data: any) => {
      const mappedData = Object.entries(
        _.groupBy(data, ({ category }) => category),
      );
      setTagOptions(mapTagsToOptions(mappedData));
    });
  }, [collectionsData?.collections]);

  const unsavedChanges = useUnsavedChangesGuard(
    asyncModel.type === 'fetched' && asyncModel.value.isDirty,
  );

  if (asyncModel.type !== 'fetched') return asyncModel;
  const model = asyncModel.value;
  const dispatch: EditWorkoutVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as EditWorkoutVMAction;
    switch (action.type) {
      case 'updateAutoplayedSettings':
        setAutoplayedSettings(action.payload);
        return null;
      case 'updateAutoplayedTitlesMap':
        setAutoplayedTitlesMap(action.payload);
        return null;
      case 'save':
        saveWorkout();
        return null;
      case 'unpublish':
      case 'archive':
      case 'removeIntro':
      case 'removeThumbnail':
      case 'clearRecommendations':
      case 'clearSchedule':
        model.dispatch(action.type);
        return null;
      case 'publish': {
        const showSingleExerciseInformation =
          builderType === 'workout' &&
          model.exercises.length === 1 &&
          !singlePublishModal.confirmed;
        if (showSingleExerciseInformation) {
          setSinglePublishModal((prev) => ({ ...prev, isOpen: true }));
          return null;
        }
        model.dispatch(action.type);
        return null;
      }
      case 'addTemplate':
        model.dispatch('addWorkoutItem', action.payload);
        return null;
      case 'uploadMedia':
        model.dispatch('uploadMedia', action.payload);
        return null;
      case 'navigateToWorkouts': {
        return navigate(PATHS.PARTNER_WORKOUTS(influencerId));
      }
      case 'addToClipboard':
        if (isAutoplayedItem(action.payload)) {
          dispatchCb?.({
            type: 'copyAutoplayedToClipboard',
            payload: { exerciseId: action.payload.id },
          });
        } else {
          copy(action.payload);
        }
        trackEvent({
          type: `Exercise Copied to Clipboard`,
          payload: {
            'Template Type': exerciseTypeToString(action.payload.type),
          },
        });
        return null;
      case 'removeWorkoutItem':
        dispatchCb?.({
          type: 'removeExercise',
          payload: { exerciseId: action.payload.exerciseId },
        });
        model.dispatch('removeWorkoutItem', action.payload.index);
        return null;
      case 'addFromClipboard': {
        model.dispatch('addFromClipboard', action.payload);
        window.scrollTo(0, document.body.scrollHeight);
        trackEvent({
          type: `Exercise Pasted from Clipboard`,
          payload: {
            'Template Type':
              action.payload.length === 1
                ? exerciseTypeToString(action.payload[0].type)
                : 'Multiple',
          },
        });
        return null;
      }
      case 'changeOrderInWorkout':
      case 'changeOrderInCircuit':
      case 'addCircuitSet':
      case 'removeCircuitSet':
      case 'addSet':
      case 'removeSet':
      case 'setSetsUnit':
      case 'addMedia':
      case 'removeMedia':
      case 'addWorkoutCircuitItem':
      case 'removeCircuitItem':
      case 'setTimeZone':
      case 'setPublishedAt':
      case 'addRunCompleteAudio':
      case 'addSetAudio':
      case 'addOutdoorSet':
      case 'addTreadmillSet':
      case 'changeRunningVariant':
      case 'changeMeasurementMode':
      case 'removeRunCompleteAudio':
      case 'removeSetAudio':
      case 'exerciseTitleUpdate':
      case 'applyHint':
      case 'setExerciseSearch':
        model.dispatch(action.type, action.payload);
        return null;
      case 'openClipboard':
        trackEvent({
          type: 'Clipboard Tapped',
          payload: {},
        });
        return null;
      case 'openLibrary':
        setLibraryState({
          isOpen: true,
          index: action.payload.index,
          circuitIndex: action.payload?.circuitIndex ?? null,
          type: action.payload.type ?? null,
          action: action.payload.action,
          parentId: action.payload.parentId,
          exerciseId: action.payload.exerciseId,
        });
        return null;
      case 'closeLibraryModal':
        setLibraryState({
          isOpen: false,
          index: 0,
          circuitIndex: null,
          type: null,
          action: 'add',
          parentId: undefined,
          exerciseId: undefined,
        });
        return null;
      case 'addTemplatesFromVideos': {
        const selectedVideos = action.payload.map((id: string) =>
          (libraryData?.getCreatorsMedia || []).find(
            (item) => item.uuid === id,
          ),
        );
        model.dispatch('addWorkoutItemsWithVideoIds', {
          videos: selectedVideos as unknown as LibraryVideo[],
          circuitIndex: libraryState.circuitIndex,
          parentId: libraryState.parentId,
        });
        setLibraryState({
          isOpen: false,
          index: 0,
          circuitIndex: null,
          type: null,
          action: 'add',
          parentId: undefined,
          exerciseId: undefined,
        });
        return null;
      }
      case 'updateExerciseMediaFromSelection': {
        const selectedVideos = action.payload.map((id: string) =>
          (libraryData?.getCreatorsMedia || []).find(
            (item) => item.uuid === id,
          ),
        );
        if (libraryState.parentId && libraryState.exerciseId) {
          model.dispatch('addMedia', {
            media: selectedVideos[0] as Media,
            index: libraryState.index || 0,
            circuitIndex: libraryState.circuitIndex,
            parentId: libraryState.parentId,
            subExerciseId: libraryState.exerciseId,
          });
        } else {
          model.dispatch('addMedia', {
            media: selectedVideos[0] as Media,
            index: libraryState.index || 0,
            circuitIndex: libraryState.circuitIndex,
          });
        }
        setLibraryState({
          isOpen: false,
          index: 0,
          circuitIndex: null,
          type: null,
          action: 'add',
        });
        return null;
      }
      case 'addVideo': {
        refetchLibrary().then((d) => {
          const media = (d.data?.getCreatorsMedia || []).find(
            (item) => item.uuid === action.payload.uuid,
          ) as Media;

          if (!media) {
            return;
          }

          if (libraryState.action === 'update') {
            model.dispatch('addMedia', {
              media,
              index: libraryState.index || 0,
              circuitIndex: libraryState.circuitIndex,
              parentId: libraryState.parentId,
              subExerciseId: libraryState.exerciseId,
            });
          } else {
            model.dispatch('addTemplateWithMedia', {
              media,
              circuitIndex: libraryState.circuitIndex,
              parentId: libraryState.parentId,
            });
          }
          setLibraryState({
            isOpen: false,
            index: 0,
            circuitIndex: null,
            type: null,
            action: 'add',
            parentId: undefined,
            exerciseId: undefined,
          });
        });

        return null;
      }
      case 'showAutoplayedSettingsModal': {
        const value = autoplayedSettings[action.payload.exerciseId || ''];
        setAutoplayedSettingsModal({ isOpen: true, value });
        return null;
      }

      case 'applyAutoplayedSettings':
        dispatchCb?.({
          type: 'updateAutoplayedSettings',
          payload: action.payload,
        });
        setAutoplayedSettings((prev) => ({
          ...prev,
          [action.payload.id]: action.payload,
        }));

        setAutoplayedSettingsModal({ isOpen: false });
        return null;
      case 'hideAutoplayedSettingsModal':
        setAutoplayedSettingsModal({ isOpen: false });
        return null;

      case 'createAutoplayed':
        model.dispatch('AddAutoplayed', action.payload);
        setAutoplayedSettingsModal({ isOpen: false });
        trackEvent({
          type: 'Autoplay Template Selected',
          payload: {
            type: action.payload.type,
          },
        });
        return null;

      case 'openExerciseTypeInfo':
        setExerciseTypeInfo(true);
        return null;
    }
  };

  const saveWorkout = async (options?: { redirect?: boolean }) => {
    setIsSaving(true);
    const saved = await model.save(options);
    setIsSaving(false);
    return saved;
  };

  const saveAndLeave = async () => {
    const saved = await saveWorkout({ redirect: false });
    saved ? unsavedChanges.leave() : unsavedChanges.dismiss();
  };

  const errorMessage = getErrorMessage(asyncModel.value.formErrors);
  const expandExercises = asyncModel.value.formErrors.exercises
    ? Object.keys(asyncModel.value.formErrors.exercises)
    : [];
  return {
    type: 'fetched',
    value: {
      titlesMap: autoplayedTitlesMap,
      autoplayedSettingsModal,
      setDispatchCb: (cb) => {
        setDispatchCb(() => cb);
      },
      model,
      dispatch,
      addFromClipboard: (values: WorkoutItem[]) =>
        dispatch('addFromClipboard', values),
      control: model.control,
      templateSelection: getTemplateSelection(builderType, dispatch),
      influencerId,
      submitting: model.submitting,
      isSaving,
      programs,
      programsLoading,
      currentBreadcrumb: getCurrentBreadcrumb(builderType, workoutId),
      intro: model.intro,
      thumbnail: model.thumbnail,
      exerciseTemplates: exercisesToTemplates({
        exercises: model.exercises,
        dispatch,
        builderType,
      }),
      tagOptions,
      watch: model.watch,
      isClass: builderType == 'class',
      withSwaps: builderType !== 'class' && !!model.watch('programId'),
      inputHints: model.inputHints,
      addTemplateFromLibrary: {
        isOpen: libraryState.isOpen,
        type: libraryState.type,
        action: libraryState.action,
        loading: libraryLoading,
        onClose: () => dispatch('closeLibraryModal'),
        onAddVideo: (value) => dispatch('addVideo', value),
        onAddSelection: (videos: { uuid: string }[]) => {
          const ids = videos.map((video) => video.uuid);
          return libraryState.action === 'update'
            ? dispatch('updateExerciseMediaFromSelection', ids)
            : dispatch('addTemplatesFromVideos', ids);
        },
      },
      isNewForm: !workoutId,

      publishAction: getPublishAction(model, dispatch),
      readonly: model.readonly,
      workoutInfoId: 'workoutInfoId',
      errorMessage,
      newWorkoutErrors: model.newWorkoutErrors,
      expandExercises: expandExercises.map((i) => +i),
      singleExercisePublishModal: {
        isOpen: singlePublishModal.isOpen,
        onClose: () =>
          setSinglePublishModal((prev) => ({ ...prev, isOpen: false })),
        onContinue: () => {
          setSinglePublishModal({ confirmed: true, isOpen: false });
          model.dispatch('publish');
        },
      },
      exerciseTypesInfoModal: {
        isOpen: exerciseTypeInfo,
        onClose: () => setExerciseTypeInfo(false),
      },
      unsavedChangesModal: {
        isOpen: unsavedChanges.blockedPath !== null,
        isSaving,
        onSave: saveAndLeave,
        onDiscard: unsavedChanges.leave,
        onCancel: unsavedChanges.dismiss,
      },
      exerciseSuggestions: model.exerciseSuggestions,
    },
  };
};

const getErrorMessage = (formErrors: FormErrors) => {
  if (isEmpty(formErrors)) return undefined;

  const onlyCollectionMissing =
    Object.keys(formErrors).length === 1 && formErrors?.programId;

  if (onlyCollectionMissing)
    return "Workout can't be published if it isn't assigned to a Collection";
  console.log(formErrors);
  return 'Required fields are missing or invalid. Check the marked fields and try again.';
};

const getPublishAction = (
  model: EditWorkoutModel,
  dispatch: EditWorkoutVMDispatcher,
) => {
  if (!!model.workoutInfo.publishedAt && !model.workoutInfo.archivedAt) {
    return {
      label: 'Archive',
      onClick: () => dispatch('archive'),
      disabled: model.disableArchiveAction,
    };
  }
  return {
    label: 'Publish',
    onClick: () => dispatch('publish'),
  };
};
